import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Download, FileSignature } from 'lucide-react';
import { PageShell } from '@/components/common/PageShell';
import { Badge, Button, Card, Spinner } from '@/components/ui';
import {
  downloadMyLetterPdf,
  getSigningConsent,
  listMyLetters,
  markLetterViewed,
  refuseLetter,
  signLetter,
} from '@/api/services/hrDocuments.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { SignaturePad } from './SignaturePad';
import { formatLetterDate, LETTER_STATUS } from './letterFormat';

/** Object URL for a fetched PDF, revoked when the document or view changes. */
function usePdfUrl(
  documentId: string | undefined,
  which: 'original' | 'completed',
  enabled: boolean
): { url: string | null; error: unknown } {
  const { data, error } = useQuery({
    queryKey: ['my-letter-pdf', documentId, which],
    queryFn: () => downloadMyLetterPdf(documentId!, which),
    enabled: Boolean(documentId) && enabled,
    staleTime: Infinity,
  });
  const url = useMemo(() => (data ? URL.createObjectURL(data) : null), [data]);
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url]
  );
  return { url, error };
}

/**
 * One letter: read it, then sign or decline when it is the viewer's turn. The
 * signature binds to the fingerprint of the exact document shown, which the
 * server re-checks; consent wording is versioned and recorded as evidence.
 */
export function SignLetterPage(): React.ReactElement {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [signature, setSignature] = useState<string | null>(null);
  const [consented, setConsented] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const viewed = useRef(false);

  const { data: letters, isLoading } = useQuery({
    queryKey: ['my-letters'],
    queryFn: listMyLetters,
  });
  const letter = (Array.isArray(letters) ? letters : []).find(
    (entry) => entry._id === documentId
  );
  const awaiting = letter?.awaitingMySignature === true;
  const showSigned = letter?.hasSignedCopy === true;
  const pdf = usePdfUrl(documentId, showSigned ? 'completed' : 'original', Boolean(letter));

  const { data: consent } = useQuery({
    queryKey: ['signing-consent'],
    queryFn: getSigningConsent,
    enabled: awaiting,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!awaiting || !documentId || viewed.current) return;
    viewed.current = true;
    void markLetterViewed(documentId).catch(() => undefined);
  }, [awaiting, documentId]);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['my-letters'] });
    void queryClient.invalidateQueries({ queryKey: ['my-letter-pdf', documentId] });
  };

  const sign = useMutation({
    mutationFn: () =>
      signLetter(documentId!, {
        signatureImage: signature!,
        consentAccepted: true,
        consentVersion: consent!.version,
        documentSha256: letter!.documentSha256,
      }),
    onSuccess: (result) => {
      toast.success(
        result.status === 'completed'
          ? 'Signed. The signed copy is ready.'
          : 'Signed. The next signer has been notified.'
      );
      setSignature(null);
      refresh();
    },
    onError: (error) => setFormError(getErrorMessage(error)),
  });

  const decline = useMutation({
    mutationFn: () => refuseLetter(documentId!, reason.trim()),
    onSuccess: () => {
      toast.success('You declined to sign. HR has been notified.');
      setDeclining(false);
      refresh();
    },
    onError: (error) => setFormError(getErrorMessage(error)),
  });

  const canSign = Boolean(signature && consented && consent && letter) && !sign.isPending;

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <div className="flex justify-center py-12" role="status">
        <Spinner />
        <span className="sr-only">Loading letter</span>
      </div>
    );
  } else if (!letter) {
    body = (
      <Card>
        <p className="text-sm text-gray-700 dark:text-gray-200">
          This letter is not available to you.
        </p>
      </Card>
    );
  } else {
    const status = LETTER_STATUS[letter.status];
    body = (
      <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <Card
          header={
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h1 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {letter.title}
              </h1>
              <Badge variant={status.variant}>{status.label}</Badge>
            </div>
          }
          contentClassName="p-0"
        >
          {pdf.url ? (
            <iframe
              title={`${letter.title}${showSigned ? ' (signed copy)' : ''}`}
              src={pdf.url}
              className="h-[70vh] min-h-[420px] w-full border-0"
            />
          ) : pdf.error ? (
            <p className="p-4 text-sm text-danger" role="alert">
              {getErrorMessage(pdf.error)}
            </p>
          ) : (
            <div className="flex justify-center py-12" role="status">
              <Spinner />
              <span className="sr-only">Loading document</span>
            </div>
          )}
          {pdf.url && (
            <div className="border-t border-gray-200 px-4 py-3 dark:border-gray-700">
              <a
                href={pdf.url}
                download={`${letter.title}${showSigned ? ' (signed)' : ''}.pdf`}
                className="inline-flex items-center text-sm font-medium text-primary hover:underline"
              >
                <Download className="mr-1.5 h-4 w-4" aria-hidden />
                Download {showSigned ? 'signed copy' : 'PDF'}
              </a>
            </div>
          )}
        </Card>

        <div className="min-w-0 space-y-4">
          {awaiting ? (
            <Card
              header={
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Sign this letter
                </h2>
              }
            >
              {declining ? (
                <form
                  className="space-y-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (reason.trim().length >= 3) decline.mutate();
                  }}
                >
                  <label
                    htmlFor="decline-reason"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-200"
                  >
                    Why are you declining? <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="decline-reason"
                    rows={3}
                    value={reason}
                    aria-required
                    onChange={(event) => setReason(event.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  />
                  {formError && (
                    <p className="text-sm text-danger" role="alert">
                      {formError}
                    </p>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeclining(false)}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      variant="danger"
                      size="sm"
                      disabled={reason.trim().length < 3}
                      loading={decline.isPending}
                    >
                      Decline to sign
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  {letter.expiresAt && (
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                      Please sign by {formatLetterDate(letter.expiresAt)}.
                    </p>
                  )}
                  <SignaturePad onChange={setSignature} disabled={sign.isPending} />
                  <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      checked={consented}
                      onChange={(event) => setConsented(event.target.checked)}
                      disabled={!consent}
                    />
                    <span>{consent?.text ?? 'Loading signing terms…'}</span>
                  </label>
                  {formError && (
                    <p className="text-sm text-danger" role="alert">
                      {formError}
                    </p>
                  )}
                  <div className="flex flex-wrap justify-between gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setFormError(null);
                        setDeclining(true);
                      }}
                    >
                      Decline
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={!canSign}
                      loading={sign.isPending}
                      onClick={() => {
                        setFormError(null);
                        sign.mutate();
                      }}
                    >
                      Sign letter
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ) : (
            <Card>
              <p className="text-sm text-gray-700 dark:text-gray-200">
                {letter.mySigner?.status === 'signed'
                  ? `You signed this letter${letter.mySigner.signedAt ? ` on ${formatLetterDate(letter.mySigner.signedAt)}` : ''}.`
                  : letter.status === 'issued' || letter.status === 'partially_signed'
                    ? 'Another signer needs to sign before you.'
                    : 'Nothing to sign.'}
              </p>
            </Card>
          )}
        </div>
      </div>
    );
  }

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Self Service', to: '/dashboard' },
        { label: 'My Letters', to: '/my-letters' },
        { label: 'Letter' },
      ]}
      icon={FileSignature}
      title="Letter"
      className="gap-0"
      hideHeader
    >
      <div className="mx-auto w-full max-w-6xl space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/my-letters')}>
          <ArrowLeft className="mr-1.5 h-4 w-4" aria-hidden />
          All letters
        </Button>
        {body}
      </div>
    </PageShell>
  );
}
